import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useMemo, useState } from 'react'

import { useGetPickerRowsByUidQuery } from 'api/canonicalPicker'
import type { UidGroupRow, UidsLookup } from 'api/canonicalPicker.types'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'

import { asInCatalog, MAX_PASTED_UIDS, uidsInPaste } from './pastedUids'

export type PasteStatus = 'checking' | 'checked' | 'failed'

export interface UidPaste {
  /** The uids looked up: the first MAX_PASTED_UIDS of those pasted */
  uids: string[]
  pastedCount: number
  status: PasteStatus
  /** The groups that hold a pasted uid, as their group or a store UID */
  groups: UidGroupRow[]
  /** The pasted uids nothing in the catalog holds */
  unmatched: string[]
}

const statusOf = (isError: boolean, isChecked: boolean): PasteStatus => {
  if (isError) return 'failed'
  return isChecked ? 'checked' : 'checking'
}

/** What a lookup of searched uids found, or where it stands */
export const uidSearchOf = (
  uids: string[],
  pastedCount: number,
  lookup: { currentData?: UidsLookup; isError: boolean }
): UidPaste => ({
  uids,
  pastedCount,
  status: statusOf(lookup.isError, lookup.currentData !== undefined),
  groups: lookup.currentData?.groups ?? [],
  unmatched: lookup.currentData?.unmatchedUids ?? [],
})

/**
 * A fixed list's search by UID: pasted uids, or a typed term shaped like a
 * UID. It finds the groups that hold them and adds nothing; a paste wins over
 * a typed term, and a paste that isn't all uids is left to the name search.
 */
const useUidSearch = (source: PickerSource, typedUid: string | null) => {
  const [pasted, setPasted] = useState<string[]>([])
  const uids = useMemo(() => {
    if (pasted.length > 0) return pasted.slice(0, MAX_PASTED_UIDS)
    return typedUid ? [asInCatalog(typedUid)] : []
  }, [pasted, typedUid])
  const isPaste = pasted.length > 0
  // currentData, so a lookup that answers after the next search never shows
  const { currentData, isError, isFetching, refetch } =
    useGetPickerRowsByUidQuery(uids.length > 0 ? { source, uids } : skipToken)

  const search = useMemo(
    (): UidPaste | null =>
      uids.length === 0
        ? null
        : uidSearchOf(uids, isPaste ? pasted.length : uids.length, {
            currentData,
            isError,
          }),
    [currentData, isError, isPaste, pasted.length, uids]
  )

  /** Takes the pasted text as uids to look up, and says whether it did */
  const pasteText = useCallback((text: string): boolean => {
    const found = uidsInPaste(text)
    if (!found) return false
    setPasted(found)
    return true
  }, [])

  const clearPaste = useCallback(() => {
    setPasted(current => (current.length === 0 ? current : []))
  }, [])

  return {
    search,
    isPaste,
    pasteText,
    clearPaste,
    retry: refetch,
    isRetrying: isFetching,
  }
}

export default useUidSearch
